// Frontend Sentry — initialized at module load, before React renders.
//
// Reads VITE_SENTRY_DSN at build time (Vite inlines `import.meta.env.*`).
// When the DSN is empty (local dev without a Sentry project) every Sentry
// call is a silent no-op — `init()` short-circuits and the SDK stays dormant.
// Production deploys to CF Pages must set VITE_SENTRY_DSN as a build env
// var; otherwise Sentry is off in prod too (fail-safe — no errors lost in
// transit because of a typo'd DSN).
//
// What we capture:
//   - Uncaught React errors (via <SentryErrorBoundary> wrapping <App />)
//   - Window-level errors + unhandledrejection events (auto via init)
//   - Manual capture calls: `captureError(err, context)`
//
// What we deliberately do NOT capture:
//   - Anything from the dev server (vite preview / npm run dev) — `environment`
//     reads import.meta.env.MODE which is "development" locally; we toggle
//     enabled=false in that mode.
//   - Request bodies / tokens / passwords — beforeSend strips known PII
//     fields from the breadcrumb URLs and tags.
//
// Source maps: Vite emits .js.map alongside .js when build.sourcemap is true
// (see vite.config.ts). CF Pages serves them at the same path; Sentry's
// symbolicator fetches them on demand. No CLI upload step needed for now.
// To add automated source-map uploads later: install @sentry/vite-plugin and
// pass authToken + org + project.

import * as Sentry from "@sentry/react";

const DSN = import.meta.env["VITE_SENTRY_DSN"] as string | undefined;
const ENV = import.meta.env["MODE"] as string | undefined; // 'development' | 'production' | 'test'
const RELEASE = import.meta.env["VITE_GIT_SHA"] as string | undefined;

/**
 * Best-effort PII scrub before events leave the browser. The current shape:
 *   - Strip `Authorization`, `Cookie`, CF-Access-* from request headers.
 *   - Drop ?token=... style query strings from breadcrumb URLs.
 *   - Truncate any field over 4KB to keep payloads small.
 * Anything matched is replaced with `[REDACTED]` so the field is still
 * visible in Sentry (operator sees "yes, there was an auth header") without
 * the value leaking.
 */
function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  const SENSITIVE_HEADER_KEYS = new Set([
    "authorization",
    "cookie",
    "cf-access-jwt-assertion",
    "cf-access-client-id",
    "cf-access-client-secret",
    "x-origin-auth",
  ]);

  if (event.request?.headers) {
    const cleaned: Record<string, string> = {};
    for (const [k, v] of Object.entries(event.request.headers)) {
      cleaned[k] = SENSITIVE_HEADER_KEYS.has(k.toLowerCase())
        ? "[REDACTED]"
        : String(v);
    }
    event.request.headers = cleaned;
  }

  // Scrub query strings from breadcrumb URLs that look auth-shaped.
  if (event.breadcrumbs) {
    for (const b of event.breadcrumbs) {
      if (b.data && typeof b.data["url"] === "string") {
        b.data["url"] = (b.data["url"] as string).replace(
          /([?&])(token|access_token|client_secret|secret)=[^&]+/gi,
          "$1$2=[REDACTED]",
        );
      }
    }
  }

  return event;
}

export function initSentry(): void {
  if (!DSN) {
    // Stay quiet in local dev / when DSN isn't configured. No console.warn —
    // the absence of a DSN is a normal state, not a bug.
    return;
  }
  if (ENV === "development" || ENV === "test") {
    // Even with a DSN set, never report from local dev or test runs.
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV ?? "production",
    release: RELEASE,
    // 10% trace sampling is plenty for an internal team tool. Adjust later
    // via a separate VITE_SENTRY_TRACES_SAMPLE_RATE if needed.
    tracesSampleRate: 0.1,
    // Session replay disabled for now — adds bundle weight and isn't free
    // tier on Sentry. Enable explicitly when an operator asks for it.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    beforeSend: scrubEvent,
  });
}

/**
 * Manual error capture from non-React code paths (api.ts catch blocks,
 * runtime polling failures, etc.). Falls through silently when Sentry
 * isn't initialized. `tags` lets the call site mark the area
 * (e.g. tags: { area: 'fetch' }) so the operator can filter in Sentry UI.
 */
export function captureError(
  err: unknown,
  context?: { tags?: Record<string, string>; extras?: Record<string, unknown> },
): void {
  if (!DSN) return;
  Sentry.captureException(err, {
    tags: context?.tags,
    extra: context?.extras,
  });
}

// Re-export the Sentry-wrapped ErrorBoundary so callers don't need to import
// the SDK directly. Used in main.tsx.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
